import { useCallback, useEffect, useState } from "react";
import { ScanFace, Check, X, UserX } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Switch } from "../components/ui/switch";
import { Dialog, DialogContent, DialogHeader, DialogTitle } from "../components/ui/dialog";
import { PageHeader, EmptyState, Field, KehadiranBadge } from "../components/common";
import { PerusahaanSelect } from "../components/inputs";
import { AuthImage } from "../components/AuthImage";
import { api, errMsg } from "../lib/api";
import { fmtDate, todayISO } from "../lib/format";

const Sum = ({ label, value, tone = "text-gray-900", testid }) => (
  <div className="rounded-xl border border-gray-200 bg-white p-4">
    <p className="text-[11px] font-semibold uppercase tracking-wider text-gray-500">{label}</p>
    <p data-testid={testid} className={`mt-1 font-mono text-2xl font-semibold ${tone}`}>{value}</p>
  </div>
);

const DetailDialog = ({ rec, onClose, onReviewed }) => {
  const [busy, setBusy] = useState(false);
  if (!rec) return null;
  const review = async (keputusan) => {
    setBusy(true);
    try { await api.post(`/kehadiran/${rec.id}/review`, { keputusan }); toast.success(keputusan === "disetujui" ? "Kehadiran disetujui" : "Kehadiran ditolak"); onReviewed(); onClose(); }
    catch (e) { toast.error(errMsg(e)); } finally { setBusy(false); }
  };
  return (
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-2xl" data-testid="kehadiran-detail-dialog">
        <DialogHeader><DialogTitle className="font-heading">{rec.anak_nama} · {rec.jam} WIB</DialogTitle></DialogHeader>
        <div className="grid grid-cols-2 gap-3">
          <div><p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Selfie check-in</p><AuthImage src={`/kehadiran/${rec.id}/foto`} className="aspect-square w-full rounded-xl" testid="kehadiran-detail-selfie" /></div>
          <div><p className="mb-1 text-[11px] font-semibold uppercase tracking-wider text-gray-500">Foto referensi</p><AuthImage src={`/anak/${rec.anak_id}/wajah/foto`} className="aspect-square w-full rounded-xl" testid="kehadiran-detail-reference" /></div>
        </div>
        <dl className="grid grid-cols-2 gap-3 text-sm sm:grid-cols-4">
          <div><dt className="text-[11px] uppercase text-gray-400">Status</dt><dd><KehadiranBadge status={rec.status} /></dd></div>
          <div><dt className="text-[11px] uppercase text-gray-400">Perusahaan</dt><dd data-testid="kehadiran-detail-perusahaan">{rec.perusahaan_nama || "-"}</dd></div>
          <div><dt className="text-[11px] uppercase text-gray-400">Kemiripan wajah</dt><dd data-testid="kehadiran-detail-face-distance" className="font-mono">{rec.face_distance ?? "tidak terdeteksi"} <span className="text-gray-400">(≤ {rec.face_threshold})</span></dd></div>
          <div><dt className="text-[11px] uppercase text-gray-400">Waktu</dt><dd data-testid="kehadiran-detail-waktu" className="font-mono">{fmtDate(rec.tanggal)} {rec.jam}</dd></div>
        </dl>
        <div className="flex flex-wrap items-center gap-2">
          <div className="flex-1" />
          {rec.status !== "terverifikasi" && (
            <>
              <Button data-testid="kehadiran-reject-btn" variant="outline" disabled={busy || rec.status === "ditolak"} className="h-10 rounded-full border-red-200 text-red-700 hover:bg-red-50" onClick={() => review("ditolak")}><X className="mr-1.5 h-4 w-4" />Tolak</Button>
              <Button data-testid="kehadiran-approve-btn" disabled={busy || rec.status === "disetujui"} className="h-10 rounded-full" onClick={() => review("disetujui")}><Check className="mr-1.5 h-4 w-4" />Setujui</Button>
            </>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default function KehadiranPage() {
  const [tanggal, setTanggal] = useState(todayISO());
  const [perId, setPerId] = useState("");
  const [onlyCheck, setOnlyCheck] = useState(false);
  const [perusahaan, setPerusahaan] = useState([]);
  const [d, setD] = useState(null);
  const [open, setOpen] = useState(null);

  useEffect(() => { api.get("/perusahaan").then((r) => setPerusahaan(r.data)); }, []);
  const load = useCallback(() => {
    if (!tanggal) return;
    api.get("/kehadiran", { params: { tanggal, perusahaan_id: perId || undefined, status: onlyCheck ? "perlu_dicek" : undefined } })
      .then((r) => setD(r.data)).catch((e) => toast.error(errMsg(e)));
  }, [tanggal, perId, onlyCheck]);
  useEffect(() => { load(); }, [load]);

  const s = d?.summary || {};
  return (
    <div>
      <PageHeader title="Kehadiran" testid="kehadiran-title" subtitle={d ? `Check-in ${fmtDate(d.tanggal)}` : "Memuat…"} />
      <div className="mb-5 grid grid-cols-2 gap-3 md:grid-cols-[200px_1fr_auto] md:items-end">
        <Field label="Tanggal"><Input type="date" data-testid="kehadiran-tanggal" className="h-10 bg-white" value={tanggal} onChange={(e) => setTanggal(e.target.value)} /></Field>
        <Field label="Perusahaan"><PerusahaanSelect perusahaan={perusahaan} value={perId} onChange={setPerId} allowAll testid="kehadiran-filter-perusahaan" /></Field>
        <label className="col-span-2 flex h-10 items-center justify-between gap-3 rounded-lg border border-gray-200 bg-white px-4 md:col-span-1">
          <span className="text-sm font-medium">Perlu dicek saja</span>
          <Switch data-testid="kehadiran-filter-perlu-dicek" checked={onlyCheck} onCheckedChange={setOnlyCheck} />
        </label>
      </div>

      <div className="mb-4 grid grid-cols-2 gap-3 lg:grid-cols-4">
        <Sum label="Hadir" value={`${s.hadir ?? 0} / ${s.total_aktif ?? 0}`} tone="text-emerald-600" testid="kehadiran-sum-hadir" />
        <Sum label="Terverifikasi" value={(s.terverifikasi ?? 0) + (s.disetujui ?? 0)} tone="text-emerald-700" testid="kehadiran-sum-verified" />
        <Sum label="Perlu dicek" value={s.perlu_dicek ?? 0} tone="text-amber-700" testid="kehadiran-sum-perlu-dicek" />
        <Sum label="Belum check-in" value={s.belum ?? 0} tone="text-gray-500" testid="kehadiran-sum-belum" />
      </div>

      <h2 className="mb-3 font-heading text-lg font-semibold text-gray-900">Sudah check-in</h2>
      {d && d.records.length === 0 ? <EmptyState icon={ScanFace} title="Belum ada check-in" desc={onlyCheck ? "Tidak ada yang perlu dicek." : "Belum ada anak yang check-in pada tanggal ini."} /> : (
        <div className="overflow-hidden rounded-xl border border-gray-200 bg-white">
          {(d?.records || []).map((r) => (
            <button key={r.id} data-testid={`kehadiran-row-${r.id}`} onClick={() => setOpen(r)} className="flex w-full items-center gap-3 border-b border-gray-100 px-4 py-3 text-left last:border-0 hover:bg-gray-50">
              <AuthImage src={`/kehadiran/${r.id}/foto`} className="h-14 w-14 shrink-0 rounded-xl" testid={`kehadiran-thumb-${r.id}`} />
              <div className="min-w-0 flex-1">
                <p className="break-words font-semibold text-gray-900">{r.anak_nama}</p>
                <p className="break-words text-[13px] text-gray-500"><span className="font-mono">{r.jam}</span> · {r.perusahaan_nama || "-"}</p>
              </div>
              <KehadiranBadge status={r.status} testid={`kehadiran-status-${r.id}`} />
            </button>
          ))}
        </div>
      )}

      {!onlyCheck && (
        <>
          <h2 className="mb-3 mt-8 font-heading text-lg font-semibold text-gray-900">Belum check-in ({d?.belum.length ?? 0})</h2>
          {d && d.belum.length === 0 ? <EmptyState icon={UserX} title="Semua anak aktif sudah check-in" /> : (
            <div className="grid gap-2 sm:grid-cols-2 lg:grid-cols-3" data-testid="kehadiran-belum-list">
              {(d?.belum || []).map((a) => (
                <div key={a.id} data-testid={`kehadiran-belum-${a.id}`} className="rounded-xl border border-dashed border-gray-300 bg-white px-4 py-3">
                  <p className="font-semibold text-gray-800">{a.nama}</p>
                  <p className="text-xs text-gray-500">{a.perusahaan_nama || "Tanpa perusahaan"}</p>
                </div>
              ))}
            </div>
          )}
        </>
      )}
      <DetailDialog rec={open} onClose={() => setOpen(null)} onReviewed={load} />
    </div>
  );
}
